import theme from "assets/theme";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet, View } from "react-native";
import CustomLink from "web/components/form-elements/CustomLink.component";
import {
  CONTACTINFO_TYPE_CELLPHONE,
  CONTACTINFO_TYPE_DEFAULT,
  CONTACTINFO_TYPE_EMERGENCY,
} from "web/constants";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import { ConfirmationContext } from "webContext/Confirmation.context";

export const TYPE_LABELS = {
  primaryAddress: "Primary Address",
  secondaryAddress: "Secondary Address",
  primaryEmail: "Primary Email",
  secondaryEmail: "Secondary Email",
  primaryPhone: "Primary Phone Number",
  secondaryPhone: "Secondary Phone Number",
};

const EMERGENCY_TYPE_OPTIONS = [
  { type: "f8fb28ac-1654-42e4-8a76-bff5a453d284", key: "primaryAddress" },
  { type: "f3037f50-bcb0-4e4d-812a-45f1a7ba0764", key: "secondaryAddress" },
  { type: "38a88f13-9dda-4071-8809-5a0c5942097f", key: "primaryEmail" },
  { type: "9aea74a9-9560-48eb-87a0-b786528046fe", key: "secondaryEmail" },
  { type: "d42c05db-67e8-47b7-86c8-52a1183e02d4", key: "primaryPhone" },
  { type: "d36339fd-595e-4522-b2c7-7ca5267d7360", key: "secondaryPhone" },
];

const classes = StyleSheet.create({
  root: {
    backgroundColor: theme.color.background,
    borderBottomRightRadius: theme.card.border.radius,
    borderBottomLeftRadius: theme.card.border.radius,
    padding: 20,
    paddingTop: 5,
  },
  deleteLink: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 15,
  },
  addButtonWrapper: {
    marginTop: 10,
  },
});

export default function EditInformationModal(props) {
  const {
    open,
    isEmergency,
    onClose,
    editInfo,
    setContactInformation,
    emergencyContactInfo,
  } = props;

  const { showConfirmation } = useContext(ConfirmationContext);
  const [title, setTitle] = useState("");
  const [data, setData] = useState("");
  const [link, setLink] = useState("");
  const isCellPhone = editInfo?.type === CONTACTINFO_TYPE_CELLPHONE;

  const normalizeExistingEmergencyContactDetails = useCallback((list) => {
    return list.map((row) => ({
      emergencyContactInformationId: row.emergencyContactInformationId,
      type: row.type,
      contactData: row.contactData || "",
    }));
  }, []);
  const [infoRows, setInfoRows] = useState(
    emergencyContactInfo?.length
      ? normalizeExistingEmergencyContactDetails(emergencyContactInfo)
      : [],
  );

  const prevOpenRef = React.useRef(false);

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;

    prevOpenRef.current = open;

    if (!justOpened) {
      return;
    }

    setTitle(editInfo?.title || "");
    setData(editInfo?.contactData || "");
    setLink(editInfo?.thirdPartyLink || "");

    if (emergencyContactInfo) {
      setInfoRows(
        emergencyContactInfo?.length
          ? normalizeExistingEmergencyContactDetails(emergencyContactInfo)
          : [],
      );
    } else if (editInfo?._associations?.EmergencyContactInformation) {
      setInfoRows(
        normalizeExistingEmergencyContactDetails(
          editInfo._associations.EmergencyContactInformation,
        ),
      );
    } else {
      setInfoRows([]);
    }
  }, [
    open,
    editInfo,
    emergencyContactInfo,
    normalizeExistingEmergencyContactDetails,
  ]);

  const handleDeleteInfoRow = useCallback((row) => {
    setInfoRows((prev) =>
      prev.filter(
        (it) =>
          (it.emergencyContactInformationId || it.tempId) !==
          (row.emergencyContactInformationId || row.tempId),
      ),
    );
  }, []);

  const createBlankRow = useCallback(
    () => ({
      tempId: Math.random(),
      type: EMERGENCY_TYPE_OPTIONS[0].type,
      contactData: "",
    }),
    [],
  );

  const handleAddInfoRow = useCallback(() => {
    setInfoRows((prev) => [...prev, createBlankRow()]);
  }, [createBlankRow]);

  const handleOnDelete = useCallback(() => {
    showConfirmation({
      prompt: "Are you sure you want to delete this contact information?",
      onConfirm: () => {
        setContactInformation((prev) => {
          return prev.filter((item) =>
            editInfo.contactInformationId
              ? item.contactInformationId !== editInfo.contactInformationId
              : item.tempId !== editInfo.tempId,
          );
        });

        onClose();
      },
    });
  }, [showConfirmation, setContactInformation, onClose, editInfo]);

  const buildUpdatedContact = useCallback(
    (type) => {
      const parentRecord = {
        contactInformationId: editInfo?.contactInformationId ?? null,
        title,
        contactData: data,
        type,
      };

      const children = infoRows.map((row) => ({
        emergencyContactInformationId:
          row.emergencyContactInformationId || null,
        contactInformationId: editInfo?.contactInformationId ?? null,
        type: row.type,
        contactData: row.contactData,
      }));

      return { parentRecord, children };
    },
    [editInfo, title, data, infoRows],
  );

  const handleOnSave = useCallback(
    (type) => {
      const { parentRecord, children } = buildUpdatedContact(type);
      setContactInformation((prev) => {
        // console.log("prev in this", prev);
        if (editInfo?.contactInformationId) {
          return prev.map((item) => {
            // console.log("item in this", item);
            return item.contactInformationId === editInfo.contactInformationId
              ? {
                  ...parentRecord,
                  infoList: children,
                }
              : item;
          });
        }
        // console.log("parentRecord in this", parentRecord);
        const isDuplicate = prev.some(
          (item) =>
            item.title === parentRecord.title &&
            item.contactData === parentRecord.contactData &&
            item.type === parentRecord.type,
        );
        // console.log("isDuplicate in this", isDuplicate);
        return [
          ...prev,
          isDuplicate
            ? {}
            : {
                ...parentRecord,
                contactInformationId: null,
                tempId: Math.random(),
                infoList: children,
              },
        ];
      });

      onClose();
    },
    [buildUpdatedContact, editInfo, setContactInformation, onClose],
  );

  // console.log("infoRows", infoRows);
  const emergencyInfoList = useMemo(() => {
    // If we have existing data from editInfo, use infoRows which should be initialized with that data. If no existing data, use infoRows for new items
    return infoRows.length > 0
      ? infoRows.filter(
          (row) =>
            !row.contactInformationId ||
            row?.contactInformationId === editInfo?.contactInformationId,
        )
      : editInfo?._associations?.EmergencyContactInformation || [];
  }, [editInfo, infoRows]);
  return isEmergency ? (
    <CustomModal
      title="Edit Emergency Contact Information"
      open={open}
      onClose={onClose}
      scroll
      canSave={data && title}
      onSave={() => handleOnSave(CONTACTINFO_TYPE_EMERGENCY)}
    >
      <View style={classes.root}>
        <LabelInput
          label="Relationship"
          value={title}
          onChangeText={setTitle}
          placeholder="e.g. Mother, Father, Spouse"
          required
          isInApp
        />
        <LabelInput
          label="Name"
          value={data}
          required
          {...(isCellPhone ? { isPhone: true } : {})}
          onChangeText={setData}
          placeholder="John Doe"
          isInApp
        />

        {emergencyInfoList.map((row) => (
          <View
            key={row.tempId || row.emergencyContactInformationId}
            style={classes.infoRow}
          >
            <LabelInput
              label="Type"
              dropdown
              value={
                TYPE_LABELS[
                  EMERGENCY_TYPE_OPTIONS.find(
                    (option) => option.type === row.type,
                  )?.key
                ]
              }
              options={EMERGENCY_TYPE_OPTIONS}
              optionLabel={(option) => TYPE_LABELS[option.key]}
              onSelect={(item) => {
                setInfoRows((prev) =>
                  prev.map((it) =>
                    (it.emergencyContactInformationId || it.tempId) ===
                    (row.emergencyContactInformationId || row.tempId)
                      ? {
                          ...it,
                          type: item.type,
                          contactInformationId:
                            editInfo?.contactInformationId ?? null,
                        }
                      : it,
                  ),
                );
              }}
              isInApp
            />

            <LabelInput
              label={
                TYPE_LABELS[
                  EMERGENCY_TYPE_OPTIONS.find(
                    (option) => option.type === row.type,
                  )?.key
                ]
              }
              value={row.contactData || ""}
              onChangeText={(text) => {
                setInfoRows((prev) =>
                  prev.map((it) =>
                    (it.emergencyContactInformationId || it.tempId) ===
                    (row.emergencyContactInformationId || row.tempId)
                      ? {
                          ...it,
                          contactData: text,
                          contactInformationId:
                            editInfo?.contactInformationId ?? null,
                        }
                      : it,
                  ),
                );
              }}
              isInApp
            />

            <CustomLink
              linkText="Delete"
              warning
              onPress={() => handleDeleteInfoRow(row)}
            />
          </View>
        ))}

        <View style={classes.addButtonWrapper}>
          <CustomButton
            title="Add Info Item"
            icon="plus-circle-outline"
            variant="dark"
            onPress={handleAddInfoRow}
          />
        </View>
      </View>
    </CustomModal>
  ) : (
    <CustomModal
      title="Edit Contact Information"
      open={open}
      onClose={onClose}
      canSave={data && title}
      onSave={() => handleOnSave(CONTACTINFO_TYPE_DEFAULT)}
      {...(!isCellPhone
        ? {
            additionalButtons: {
              between: [
                {
                  title: "Delete",
                  variant: "danger",
                  icon: "delete",
                  disabled: !editInfo,
                  onPress: handleOnDelete,
                },
              ],
            },
          }
        : {})}
    >
      <View style={classes.root}>
        <LabelInput
          label="Title"
          value={title}
          onChangeText={setTitle}
          placeholder="Email, Work Phone, etc."
          required
          isInApp
        />
        <LabelInput
          label="Contact Data"
          value={data}
          required
          {...(isCellPhone ? { isPhone: true } : {})}
          onChangeText={setData}
          placeholder={isCellPhone ? "123-456-7890" : "email@sample.com"}
          isInApp
        />
        {!isCellPhone && (
          <LabelInput
            label="Third-Party Link"
            value={link}
            onChangeText={setLink}
            isInApp
          />
        )}
      </View>
    </CustomModal>
  );
}
